import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  ArrowLeft, Save, Building, MapPin, Mail, Phone, User 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { plantService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';

const PlantSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .required('Plant name is required'),
  isActive: Yup.boolean()
});

const PlantForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch plant for editing
  const {
    data: plantsData,
    isLoading: isLoadingPlant,
    error: plantError
  } = useQuery(
    ['plant', id],
    () => plantService.getPlantById(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000
    }
  );

  const plant = plantsData?.data;
  // Create/Update plant mutation
  const mutation = useMutation(
    (data) => id ? plantService.updatePlant(id, data) : plantService.createPlant(data),
    {
      onSuccess: () => {
        toast.success(id ? 'Plant updated successfully' : 'Plant created successfully');
        queryClient.invalidateQueries('plants');
        navigate('/plants');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || `Failed to ${id ? 'update' : 'create'} plant`);
      }
    }
  );

  // Initial values for form
  const getInitialValues = () => {
    if (plant) {
      return {
        name: plant.name || '',
        isActive: plant.isActive !== undefined ? plant.isActive : true
      };
    }
    return {
      name: '',
      isActive: true
    };
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    try {
      await mutation.mutateAsync(values);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  if (isLoadingPlant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading plant..." />
      </div>
    );
  }

  if (plantError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Error loading plant</h3>
          <p className="mt-1 text-sm text-gray-500">{plantError.message}</p>
          <button
            onClick={() => navigate('/plants')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Plants
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/plants')}
            className="mr-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit Plant' : 'Create Plant'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {id ? 'Update plant information' : 'Add a new plant location'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Plant Details</h3>
        </div>
        <div className="px-6 py-4">
          <Formik
            initialValues={getInitialValues()}
            validationSchema={PlantSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, isSubmitting }) => (
              <Form className="space-y-6">
                {/* Plant Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Plant Name *
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <Field
                      type="text"
                      name="name"
                      id="name"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter plant name"
                    />
                  </div>
                  <ErrorMessage name="name" component="p" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Status */}
                <div>
                  <div className="flex items-center">
                    <Field
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      Active Plant
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Inactive plants will not be available for compliance assignments
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/plants')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting || isSubmitting ? 'Saving...' : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default PlantForm; 